import React, { useEffect, useState } from "react";
// Adjust the path as necessary
import { Link } from "react-router-dom";
import { Table, Button, Row, Col, Form, Card } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  bulkDeleteUsers,
  deleteUser,
  getUsers,
  resendExternalInvite,
  updateUserFromExcel,
} from "../../../../services/UsersServices";

import {
  DatatableWrapper,
  TableBody,
  TableHeader,
  PaginationOptions,
  Pagination
} from "react-bs-datatable";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setMessage(""); // Clear previous messages
  };

   // Handle file upload
   const handleUpload = async () => {
    if (!selectedFile) {
      setMessage("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await updateUserFromExcel(formData);
      setMessage(response);
    } catch (error) {
      console.error("Upload error:", error);
      setMessage("Error uploading file.");
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
      setSelectedUserIds([]);
    } catch (error) {
      console.error("Failed to fetch users", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteUser(id);
      fetchUsers(); // Refresh the list after deletion
      console.log("Roles deleted successfully");
    } catch (error) {
      console.error("Error deleting role:", error);
    }
  };

  const handleToggleUserSelection = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((userId) => userId !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllFiltered = (checked, filteredRows) => {
    const filteredIds = filteredRows.map((row) => row._id);
    if (checked) {
      setSelectedUserIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
      return;
    }
    setSelectedUserIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
  };

  const handleResendInvite = async (email) => {
    try {
      const res = await resendExternalInvite({ email });
      const tempPassword = res?.tempPassword;
      const delivery = res?.delivery;
      const smtpMissing = res?.smtpMissing;
      await Swal.fire({
        icon: "success",
        title: "External user re-invited",
        text:
          delivery === "sent"
            ? "Temporary password email was sent."
            : delivery === "smtp_not_configured"
              ? `Invite re-issued, but email was not sent. Missing SMTP env: ${(
                  smtpMissing || []
                ).join(", ")}`
              : "Invite re-issued, but email was not sent.",
        width: 520,
        confirmButtonColor: "#3a7afe",
      });

      if (tempPassword) {
        await Swal.fire({
          icon: "info",
          title: "Temporary password",
          text: tempPassword,
          width: 520,
          confirmButtonColor: "#3a7afe",
        });
      }
    } catch (error) {
      const apiMsg = error?.response?.data?.data || "Failed to re-invite external user.";
      Swal.fire({
        icon: "error",
        title: "Re-invite failed",
        text: apiMsg,
        width: 520,
        confirmButtonColor: "#3a7afe",
      });
    }
  };

  const handleBulkDelete = async (filteredRows) => {
    const selectedRows = filteredRows.filter((row) => selectedUserIds.includes(row._id));
    if (selectedRows.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No users selected",
        text: "Please select at least one user.",
        confirmButtonColor: "#3a7afe",
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete selected users?",
      text: `This will delete ${selectedRows.length} user(s).`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    let successCount = 0;
    let failCount = selectedRows.length;
    try {
      const response = await bulkDeleteUsers(selectedRows.map((row) => row._id));
      successCount = Number(response?.deleted || 0);
      failCount = Math.max(0, selectedRows.length - successCount);
    } catch (error) {
      console.error("Bulk delete failed:", error);
    }
    await Swal.fire({
      icon: failCount > 0 ? "warning" : "success",
      title: "Bulk delete complete",
      text: `Deleted: ${successCount}, Failed: ${failCount}`,
      confirmButtonColor: "#3a7afe",
    });
    fetchUsers();
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Format data to match the table format (if necessary)
  const tableData = users.map((user) => ({
    fullName: user.firstName + " " + user.lastName,
    email: user.email,
    role: user.role,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    mustChangePassword: user.mustChangePassword,
    _id: user._id, // Add this for the actions to work
  }));

  // Filter the data based on employee name
  const filteredData = tableData.filter((item) =>
    item.fullName?.toString().toLowerCase().includes(filterText.toLowerCase())
  );
  const isAllFilteredSelected =
    filteredData.length > 0 && filteredData.every((row) => selectedUserIds.includes(row._id));

  // Table header configuration
  const header = [
    {
      prop: "bulkSelect",
      title: (
        <Form.Check
          type="checkbox"
          checked={isAllFilteredSelected}
          onChange={(e) => handleToggleSelectAllFiltered(e.target.checked, filteredData)}
          aria-label="Select all filtered users"
        />
      ),
      cell: (row) => (
        <Form.Check
          type="checkbox"
          checked={selectedUserIds.includes(row._id)}
          onChange={() => handleToggleUserSelection(row._id)}
          aria-label={`Select ${row.fullName}`}
        />
      ),
    },
    { prop: "fullName", title: "Full Name" },
    { prop: "email", title: "Email" },
    { prop: "role", title: "Role" },
    { prop: "createdAt", title: "Created At" },
    { prop: "updatedAt", title: "Updated At" },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <>
          <Link
            to={`/settings/users/edit/${row._id}`}
            className="btn btn-primary shadow btn-xs sharp me-1"
          >
            <i className="fa fa-pencil"></i>
          </Link>
          <Button
            variant="danger"
            className="btn btn-danger shadow btn-xs sharp me-1"
            onClick={() => handleDelete(row._id)}
          >
            <i className="fa fa-trash"></i>
          </Button>
          {String(row.role || "").toLowerCase() === "external" &&
          row.mustChangePassword === true ? (
            <Button
              variant="warning"
              className="btn btn-warning shadow btn-xs me-1 d-inline-flex align-items-center"
              title="Send invite again with new temporary password"
              onClick={() => handleResendInvite(row.email)}
            >
              <i className="fa fa-envelope me-1"></i>
              Re-invite
            </Button>
          ) : null}
        </>
      ),
    },
  ];

  return (
    <Card>
      <Card.Body>
        <h1>User List</h1>
        <Row className="mb-4">
            <Col md={3} lg={3}
              className="d-flex flex-col justify-content-end align-items-end"
            >
              <Form.Control
                type="text"
                placeholder="Search Employee Name"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </Col>
            <Col md={7} lg={7} >
              <Form className="d-flex align-items-center">
                <Form.Group controlId="formFile" className="mb-0 mr-2">
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    style={{ display: "inline-block", width: "auto" }}
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  className="ml-2"
                  onClick={handleUpload}
                  style={{marginLeft: "5px", marginRight: "5px"}}
                >
                  Upload
                </Button>
                {message && <span className="" style={{"marginLeft": "10px"}}>{message}</span>}
              </Form>
             
            </Col>
            <Col md={2} lg={2} className="d-flex justify-content-end">
              <div className="d-flex gap-2">
                {selectedUserIds.length > 1 ? (
                  <Button
                    variant="outline-danger"
                    onClick={() => handleBulkDelete(filteredData)}
                  >
                    Delete Selected
                  </Button>
                ) : null}
                <Link to="/settings/users/invite-external">
                  <Button variant="outline-primary">Invite External</Button>
                </Link>
                <Link to="/settings/users/add">
                  <Button variant="primary">Add User</Button>
                </Link>
              </div>
            </Col>
          </Row>
        <DatatableWrapper
          body={filteredData} // Use filtered data for the table
          headers={header}
          paginationOptionsProps={{
            initialState: {
              rowsPerPage: 5, // Set rows per page
              options: [5, 10, 15], // Options for rows per page
            },
          }}
        >
          <Table>
            <TableHeader />
            <TableBody />
          </Table>
          <Row>
            <Col className="d-flex flex-col justify-content-start">
              <PaginationOptions />
            </Col>
            <Col className="mt-4 d-flex flex-col justify-content-end align-items-end">
              <Pagination />
            </Col>
          </Row>
        </DatatableWrapper>
      </Card.Body>
    </Card>
  );
};

export default UserList;
